import { engineComponent, NumberUtils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { Symbols } from "../GameComponents/Symbols";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { BaseView } from "../GameComponents/Base/BaseView";
import { Paylines } from "../GameComponents/Paylines";
import { Communication } from "neo-games";
import { Symbol } from "../GameComponents/Symbol";
import { Payline } from "../GameComponents/Payline";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";
import { Tweens } from "../Components/Effects/Tweens";
import { BetManager } from "../Managers/BetManager";
import { PlayList } from "../Components/Sounds/PlayList";
import { AppContext } from "../../Common/AppContext";
import { ReelExtended } from "./ReelExtended";
import { DataManager } from "../Managers/DataManager";
import { Settings } from "./Settings";
import { GroupTweens } from "./GroupTweens";
import { TimelineElement } from "../Components/Elements/Graphics/TimelineElement";
import { AmbientBackground } from "../Utils/AmbientBackground";

@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimatorExtended extends WinAnimator {
    private _paylines: Paylines;
    private _cycleAnimationName: string = "Value";
    private _paylineWinDelay: number = 63;
    private _paylineWinEndDelay: number = 80;

    private _activeElementsNames: Array<string> = [];
    private _activeElements: Array<SwitchElement> = [];
    private _activeElementsCase: string;

    protected animationsRegular: Array<TimelineElement>;
    protected animationRegularNames: string[];
    protected animationsRegularStopped: boolean = true;

    private wildMask: boolean[] = [];

    public static readonly CaseNormal = "NORMAL";
    protected static readonly CaseWin = "WIN";
    protected static readonly CaseNoWin = "NOWIN";
    protected static readonly CaseHighWin = "HIGHWIN";
    public static readonly CaseTeasing = "TEASING";
    public static readonly CaseTeasingStop = "TEASING_STOP";

    private _wildExpanded: ContainerElement;
    private _wildsExpanded: SwitchElement[] = [];
    private _wildDepthLow = 3;
    private _wildDepthHigh = 9;
    private _middleground: SwitchElement;
    private _lineTable: SwitchElement;
    private _lineNumber: SwitchElement;
    private _crosses: SwitchElement;
    private _label500x: SwitchElement;
    private currentCounterStep: number;
    private wildWin: boolean = false;
    private paylineOrder: number;
    private _parent: BaseView = null;

    get isWildWin(): boolean {
        return this.wildWin;
    }

    protected static readonly ShowFirstWinsSum = "showFirstWinsSum";
    protected static readonly ShowFirstWinsSumAfter = "showFirstWinsAfter";
    protected static readonly ShowFirstWinsStep = "showFirstWinsStep";
    protected static readonly ShowAnimationsDelayedBefore = "showAnimationsDelayedBefore";
    protected static readonly ShowAnimationsDelayed = "showAnimationsDelayed";
    protected static readonly EndFirstWinsStep = "endFirstWinsStep";
    protected static readonly FinishWin = "finishWin";

    protected static readonly DelaySum = 8; // eye -> expand
    protected static readonly DelayPerReel = 0; // per-reel addition for eye -> expand (so animation can finish on later reels)
    protected static readonly DelaySumAfter = 120; // normal expand -> win animations
    protected static readonly ExpandDuration = 70; // win animation -> background & side tables
    protected static readonly ExpandSymbolDuration = 25; // in expand, time to hide all symbols on reel
    protected static readonly ExpandStaggerWin = 5; // per-reel delay during expand WIN
    protected static readonly ExpandStaggerCycle = 7; // per-reel delay during expand CYCLE
    protected static readonly SumDuration = 40; // duration of sum animation counter

    private _bgElementName: string = "";
    private _bgWinsIndex: Array<number>;
    private _bgScaleX: number = 1;
    private _bgScaleXMul: number = 0;
    private _bgScaleXRankFrom: number = 0;
    private _bgScaleXRankTo: number = 0;

    public _groupTweensWA: SwitchElement;

    private _wasOutro: boolean = true;

    constructor() {
        super();
    }

    configure(node: Element): void {
        super.configure(node);

        if (node.hasAttribute(WinAnimatorExtendedAttributes.CYCLE_ANIMATION)) {
            this._cycleAnimationName = node.getAttribute(WinAnimatorExtendedAttributes.CYCLE_ANIMATION);
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.ACTIVE_ELEMENTS)) {
            this._activeElementsNames = node.getAttribute(WinAnimatorExtendedAttributes.ACTIVE_ELEMENTS).split(";");
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.WILD_DEPTH_LOW)) {
            this._wildDepthLow = parseInt(node.getAttribute(WinAnimatorExtendedAttributes.WILD_DEPTH_LOW));
        }
        if (node.hasAttribute(WinAnimatorExtendedAttributes.WILD_DEPTH_HIGH)) {
            this._wildDepthHigh = parseInt(node.getAttribute(WinAnimatorExtendedAttributes.WILD_DEPTH_HIGH));
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.BG_WINS_INDEX)) {
            let stringIndex = node.getAttribute(WinAnimatorExtendedAttributes.BG_WINS_INDEX);

            if (stringIndex != null) {
                this._bgWinsIndex = stringIndex.split(";").map((value) => parseInt(value));
            }
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.BG_ELEMENT_NAME)) {
            this._bgElementName = node.getAttribute(WinAnimatorExtendedAttributes.BG_ELEMENT_NAME);
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.BG_SCALE_X)) {
            this._bgScaleX = parseFloat(node.getAttribute(WinAnimatorExtendedAttributes.BG_SCALE_X));
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.BG_SCALE_X_MULTIPLIER)) {
            this._bgScaleXMul = parseFloat(node.getAttribute(WinAnimatorExtendedAttributes.BG_SCALE_X_MULTIPLIER));
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.BG_RANK_FROM)) {
            this._bgScaleXRankFrom = parseFloat(node.getAttribute(WinAnimatorExtendedAttributes.BG_RANK_FROM));
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.BG_RANK_TO)) {
            this._bgScaleXRankTo = parseFloat(node.getAttribute(WinAnimatorExtendedAttributes.BG_RANK_TO));
        }

        if (node.hasAttribute("WinsRegular")) {
            this.animationRegularNames = node.getAttribute("WinsRegular").split(";");
        }
    }

    initialize(
        symbols: Symbols,
        paylines?: Paylines,
        noWinSymbolsContainer?: ContainerElement,
        symbolsContainer?: ContainerElement,
        parent?: BaseView,
    ): void {
        this._paylines = paylines;

        this._parent = parent;

        super.initialize(symbols, paylines, noWinSymbolsContainer, symbolsContainer);

        if (!this.value && this._cycleAnimationName && this.childs[this._cycleAnimationName]) {
            this.value = this.childs[this._cycleAnimationName];
            this.animationsContainer.addChild(this.value);
        }

        if (this.getChildElementByName("Win500")) {
            this._label500x = this.getChildElementByName("Win500") as SwitchElement;
        }

        if (parent) {
            this._activeElementsNames.forEach((elementName) => {
                if (parent.childs[elementName] && parent.childs[elementName] instanceof SwitchElement) {
                    this._activeElements.push(parent.childs[elementName]);
                }
            });

            if (parent.getChildElementByName("WildExpanded")) {
                this._wildExpanded = parent.getChildElementByName("WildExpanded") as ContainerElement;
                for (let key in this._wildExpanded.childs) {
                    if (this._wildExpanded.childs[key] instanceof SwitchElement) {
                        this._wildsExpanded.push(this._wildExpanded.childs[key]);
                    }
                }
            }

            if (parent.getChildElementByName("GroupTweens")) {
                this._groupTweensWA = parent.getChildElementByName("GroupTweens") as SwitchElement;
            }

            if (parent.getChildElementByName("Middleground")) {
                this._middleground = parent.getChildElementByName("Middleground") as SwitchElement;
                if (this._middleground.getChildElementByName("LineTable")) {
                    this._lineTable = this._middleground.getChildElementByName("LineTable") as SwitchElement;
                }

                if (this._middleground.getChildElementByName("LineNumber")) {
                    this._lineNumber = this._middleground.getChildElementByName("LineNumber") as SwitchElement;
                }
            }

            if (parent.getChildElementByName("Crosses")) {
                this._crosses = parent.getChildElementByName("Crosses") as SwitchElement;
            }
        }
    }

    protected createAnimations(): void {
        super.createAnimations();

        this.animationsRegular = [];

        for (let i = 0; i < this.animationRegularNames.length; i++) {
            if (this.childs[this.animationRegularNames[i]]) {
                let animation = this.childs[this.animationRegularNames[i]];
                animation.visible = false;

                this.animationsRegular.push(animation);

                if (i == 1) {
                    animation.on("finished", this.onAnimationFinished.bind(this));
                    animation.on(
                        TimelineElement.AnimationComplete,
                        function () {
                            this.animationsRegularStopped = true;
                            this.onAnimationComplete();
                        }.bind(this),
                    );
                } else {
                    animation.on(
                        TimelineElement.AnimationComplete,
                        function () {
                            this.animationsRegularStopped = true;
                        }.bind(this),
                    );
                }

                this.animationsContainer.addChild(animation);
            }
        }

        for (let animation of this.animations) {
            animation.on(
                "hw_intro",
                function () {
                    this._wasOutro = false;
                    if (this._groupTweensWA) {
                        this._groupTweensWA.goToCase(GroupTweens.CaseHighwinsIntro);
                    }
                }.bind(this),
            );
            animation.on(
                "hw_soft_outro",
                function () {
                    this._wasOutro = true;
                    if (this._groupTweensWA) {
                        this._groupTweensWA.goToCase(GroupTweens.CaseHighwinsSoftOutro);
                    }
                }.bind(this),
            );
        }
    }

    protected onAnimationComplete() {
        super.onAnimationComplete();
    }

    protected stopAnimation(): void {
        if (this._groupTweensWA) {
            this._groupTweensWA.goToCase(GroupTweens.CaseHighwinsHardOutro);
        }

        super.stopAnimation();
    }

    //******************************************************************************************************************
    //  SYMBOLS
    //******************************************************************************************************************

    /**
     * Sorts symbols according to their zIndex attribute, if applicable
     */
    private sortSymbols(): void {
        if (!this.symbols.hasZindex) {
            return;
        }
        let addQueue: Symbol[] = [];
        this.symbolsContainer.children.forEach((symbol) => {
            addQueue.push(<Symbol>symbol);
        });
        addQueue.sort((a, b) => a.zIndex - b.zIndex);
        addQueue.forEach((symbol) => {
            this.symbolsContainer.addChild(symbol);
        });
    }

    showSymbols(reelsData: Array<Communication.Game.IReel>) {
        super.showSymbols(reelsData);
        this.sortSymbols();
    }

    // public symbolsGoToTeasing(reelIndex: number, isSecondChance: boolean): void {
    //     for (let i = 0; i < reelIndex; i++) {
    //         for (let j = 0; j < this.currentSymbols[i].length; j++) {
    //             const symbol = this.currentSymbols[i][j];
    //             if (symbol !== null) {
    //                 if(isSecondChance) {
    //                     if (this.allowSymbolGoToCase(i, j, Symbol.CaseNowin)) {
    //                         const currentCaseType = symbol.getCurrentCase();
    //
    //                         if (Symbol.CaseNowin !== currentCaseType) {
    //                             symbol.goToCase(Symbol.CaseNowin);
    //                         }
    //                     }
    //                 } else {
    //                     if (symbol.id !== Settings.SymbolTeasing-1 && this.allowSymbolGoToCase(i, j, Symbol.CaseNowin)) {
    //                         const currentCaseType = symbol.getCurrentCase();
    //
    //                         if (Symbol.CaseNowin !== currentCaseType) {
    //                             symbol.goToCase(Symbol.CaseNowin);
    //                         }
    //                     } else if(symbol.id === Settings.SymbolTeasing-1 && this.allowSymbolGoToCase(i, j, SymbolExtended.CaseTeasingNormal)) {
    //                         const currentCaseType = symbol.getCurrentCase();
    //
    //                         if (SymbolExtended.CaseTeasingNormal !== currentCaseType) {
    //                             symbol.goToCase(SymbolExtended.CaseTeasingNormal);
    //                         }
    //                     }
    //                 }
    //             }
    //         }
    //     }
    // }

    onReelStopped(reelsData: Array<Communication.Game.IReel>, reelIndex: number) {
        super.onReelStopped(reelsData, reelIndex);

        if (ReelExtended.Tease) {
            for (let i = 0; i < this.currentSymbols.length - 1; i++) {
                for (let j = 0; j < this.currentSymbols[i].length; j++) {
                    const symbol = this.currentSymbols[i][j];
                    if (symbol !== null && symbol.id !== Settings.SymbolTeasing - 1) {
                        //if (symbol != null && j != 1) {
                        symbol.goToCase(Symbol.CaseNowin);
                    }
                }
            }

            if (reelIndex == this.reelCount - 1) {
                for (let i = 0; i < this.currentSymbols.length - 1; i++) {
                    for (let j = 0; j < this.currentSymbols[i].length; j++) {
                        const symbol = this.currentSymbols[i][j];
                        if (symbol !== null && symbol.id !== Settings.SymbolTeasing - 1) {
                            //if (symbol != null && j != 1) {
                            symbol.goToCase(Symbol.CaseNormal);
                        }
                    }
                }
            }
        }

        if (this.currentSymbols[reelIndex]) {
            this.currentSymbols[reelIndex].forEach((symbol) => {
                if (symbol.hasCase("IMPACT")) {
                    symbol.goToCase("IMPACT");
                }
            });
        }
        this.sortSymbols();
    }

    //******************************************************************************************************************
    //  WINS
    //******************************************************************************************************************

    protected showFirstWins(): void {
        if (this._parent) {
            this._parent.addChildAt(this._wildExpanded, this._wildDepthHigh);
        }

        this.wildMask = [false, false, false];
        if (this.currentWins) {
            this.currentWins.forEach((win) => {
                if (win.trigger === Communication.Game.WinTrigger.Way) {
                    for (let i = 0; i < win.symbols.length; i++) {
                        if (win.symbols[i] == 10) {
                            this.wildMask[i] = true;
                            this.wildWin = true;
                        }
                    }
                }
                if (win.trigger === Communication.Game.WinTrigger.Scatter) {
                    this.wildMask = [true, true, true];
                    this.wildWin = true;
                }
            });
        }

        let isScatter = false;
        let furthestWild = 0;

        if (this.currentWins) {
            this.currentWins.forEach((win) => {
                if (win.trigger === Communication.Game.WinTrigger.Way) {
                    this.wildWin = true;
                    if (win.symbols && furthestWild < this.reelCount) {
                        for (let i = 0; i < win.symbols.length; i++) {
                            if (win.symbols[i] == 10 && i > furthestWild) {
                                furthestWild = i;
                            }
                        }
                    }
                }
                if (win.trigger === Communication.Game.WinTrigger.Scatter) {
                    isScatter = true;
                    this.wildWin = true;
                }
            });
        }

        furthestWild = BaseView.Quickstopped ? 0 : furthestWild * WinAnimatorExtended.DelayPerReel;

        if (this.currentWin > 0) {
            this._paylines.visible = true;
        }

        this.animationsRegular[0].visible = false;
        this.animationsRegular[0].alpha = 0;
        this.animationsRegular[2].visible = false;
        this.animationsRegular[2].alpha = 0;

        if (isScatter) {
            this._lineTable.goToCase("HIDE");
            this._crosses.goToCase("SHOW_SCATTER");
            PlayList.play("SND_BaseExpandWild");
            this.setActiveElementCaseArray(["SHOW_SCATTER"], false, furthestWild);
            AmbientBackground.changeAmbientBackground("color-1", 1000, 1000);
        } else if (this.wildWin) {
            this._lineTable.goToCase("SHOW_81", false, WinAnimatorExtended.ExpandDuration + furthestWild);
            this._crosses.goToCase("SHOW_81", false, WinAnimatorExtended.ExpandDuration + furthestWild);
            this.setActiveElementCaseArray(["SHOW_81"], false, furthestWild);
            AmbientBackground.changeAmbientBackground("color-1", 1000, 1000);
        }

        if (this.currentWin === BetManager.Bet) {
            super.showFirstWins();
        } else if (this.currentMultiplierIndex < 2 || (this.wildWin && !isScatter)) {
            this.currentCounterStep = 0;
            this.currentAnimationIndex = this.getAnimationIndex();
            this.currentAnimationFinished = false;
            if (this.currentAnimationIndex >= 2) {
                // nop
            } else if (this.currentWin < BetManager.Bet && this.animationsRegular[0]) {
                this.animationsRegular[0].visible = true;
                this.animationsRegular[0].alpha = 0;
                this.animationsRegularStopped = false;
            } else if (this.currentWin > BetManager.Bet && this.animationsRegular[2]) {
                this.animationsRegular[2].visible = true;
                this.animationsRegular[2].alpha = 0;
                this.animationsRegularStopped = false;
            }
            this.startTimer(
                this.wildWin ? WinAnimatorExtended.ShowFirstWinsSum : WinAnimatorExtended.ShowFirstWinsStep,
                this.wildWin ? WinAnimatorExtended.DelaySum + furthestWild : 15,
            );
        } else {
            if (isScatter) {
                this.currentAnimationIndex = this.getAnimationIndex();
                let animation =
                    this.currentWin === BetManager.Bet && this.animationsRegular[1]
                        ? this.animationsRegular[1]
                        : this.animations[this.currentAnimationIndex];
                animation.visible = true;
                this.currentAnimationFinished = false;
                this.animationsRegularStopped = false;
                this.startTimer(WinAnimatorExtended.ShowAnimationsDelayedBefore, 30);
            } else {
                PlayList.setVolume("SND_BaseLoop", 0);
                PlayList.play("SND_HWIntro");
                super.showFirstWins();
            }
        }
    }

    private playWinSound(): void {
        let lowMultiplier: number = parseFloat(NumberUtils.devide(this.currentWin, BetManager.Bet));
        PlayList.play("SND_BaseWinlineHit");
        if (lowMultiplier > 5 && lowMultiplier <= 10) {
            PlayList.play("SND_BaseWin02");
        } else if (lowMultiplier > 10) {
            PlayList.play("SND_BaseWin03");
        } else {
            PlayList.play("SND_BaseWin01");
        }
    }

    protected startAnimation(noSpecialWin: boolean = false) {
        this.currentAnimationIndex = this.getAnimationIndex();
        this.currentAnimationFinished = false;

        if (this.currentAnimationIndex >= 2) {
            this.setActiveElementCaseArray(["HIGHWINS"]);
        }

        let animation =
            this.currentWin === BetManager.Bet && this.animationsRegular[1]
                ? this.animationsRegular[1]
                : this.animations[this.currentAnimationIndex];

        this.animationsRegularStopped = false;
        animation.visible = true;

        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            ImageTextCounterElement.Round = true;
            animation.play(
                parseInt(NumberUtils.devide(this.currentWin, BetManager.CoinSize).toString()),
                this.currentWin === BetManager.Bet && this.wildWin ? 10 : undefined,
            );
        } else {
            ImageTextCounterElement.Round = false;
            animation.play(this.currentWin, 0, this.currentWin === BetManager.Bet && this.wildWin ? 10 : undefined);
        }
    }

    protected showFirstInitWins() {
        this.wildMask = [false, false, false];
        if (this.currentWins) {
            this.currentWins.forEach((win) => {
                if (win.trigger === Communication.Game.WinTrigger.Way) {
                    for (let i = 0; i < win.symbols.length; i++) {
                        if (win.symbols[i] == 10) {
                            this.wildMask[i] = true;
                            this.wildWin = true;
                        }
                    }
                }
                if (win.trigger === Communication.Game.WinTrigger.Scatter) {
                    this.wildMask = [true, true, true];
                    this.wildWin = true;
                }
            });
        }

        super.showFirstInitWins();

        if (this._parent) {
            this._parent.addChildAt(this._wildExpanded, this._wildDepthHigh);
        }

        if (this.currentWin > 0) {
            this._paylines.visible = true;
            this.wildMask.forEach((value, i) => {
                if (value) {
                    this._wildsExpanded[i].goToCase("STATIC");
                    this._wildsExpanded[i].goToCase("ANIMATE");
                    this.currentSymbols[i].forEach((symbol) => (symbol ? symbol.goToCase("EMPTY") : null));
                }
            });
        }

        if (this.wildWin) {
            if (
                this.currentWins &&
                this.currentWins.filter((win) => win.trigger === Communication.Game.WinTrigger.Scatter).length
            ) {
                this._lineTable.goToCase("INIT_HIDE");
                this._crosses.goToCase("SHOW_INIT_SCATTER");
                this.setActiveElementCaseArray(["SHOW_INIT_SCATTER"]);
                AmbientBackground.changeAmbientBackground("color-1", 0, 0);
                this.value.visible = false;
            } else {
                // this._lineTable.goToCase("SHOW_81_INIT");
                this.setActiveElementCaseArray(["SHOW_81_INIT", "SHOW_81"]);
                this._crosses.goToCase("SHOW_INIT_81");
                AmbientBackground.changeAmbientBackground("color-1", 0, 0);
            }
        }
    }

    protected winSymbolsGoToCaseAsWay(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];

        for (let i = 0; i < this.reelCount; i++) {
            for (let j = 0; j < this.symbolCount; j++) {
                if ((win.mask[i] & (1 << j)) !== 0 && this.allowSymbolGoToCase(i, j, caseType)) {
                    const symbol = this.currentSymbols[i][j];

                    if (!win.symbols || win.symbols[i] === 10) {
                        symbol.goToCase(
                            "EMPTY",
                            false,
                            i * WinAnimatorExtended.ExpandStaggerCycle +
                                j * (WinAnimatorExtended.ExpandSymbolDuration / this.symbolCount),
                        );
                        if (Math.pow(2, j) == win.mask[i]) {
                            switch (caseType) {
                                case "WIN":
                                    this._wildsExpanded[i].goToCase("STATIC");
                                    break;
                                case "CYCLE":
                                    this._wildsExpanded[i].goToCase(
                                        "WIN_" + j,
                                        false,
                                        i * WinAnimatorExtended.ExpandStaggerCycle,
                                    );
                                    break;
                            }
                        }
                    } else {
                        switch (caseType) {
                            case "WIN":
                                symbol.goToCase(caseType, false, i * WinAnimatorExtended.ExpandStaggerWin);
                                break;
                            case "CYCLE":
                                symbol.goToCase(caseType, false, i * WinAnimatorExtended.ExpandStaggerCycle);
                                break;
                            default:
                                symbol.goToCase(caseType, false);
                                break;
                        }
                    }

                    this.symbolsContainer.addChild(symbol);

                    symbols.push(symbol);
                }
            }
        }

        return symbols;
    }

    protected winSymbolsGoToCaseAsScatter(winIndex: number, caseType: string): Array<Symbol> {
        const symbols: Array<Symbol> = [];

        for (let i = 0; i < this.reelCount; i++) {
            for (let j = 0; j < this.symbolCount; j++) {
                const symbol = this.currentSymbols[i][j];

                symbol.goToCase(
                    "EMPTY",
                    false,
                    i * WinAnimatorExtended.ExpandStaggerWin +
                        j * (WinAnimatorExtended.ExpandSymbolDuration / this.symbolCount),
                );

                symbols.push(symbol);
            }

            switch (caseType) {
                case "WIN":
                    this._wildsExpanded[i].goToCase("ANIMATE");
                    this._wildsExpanded[i].goToCase("ON_WIN", false, i * WinAnimatorExtended.ExpandStaggerWin);
                    break;
                case "CYCLE":
                    this._wildsExpanded[i].goToCase("WIN_FULL", false, i * WinAnimatorExtended.ExpandStaggerCycle);
                    break;
            }
        }

        return symbols;
    }

    protected allSymbolsGoToCase(caseType: string, noWin: boolean, force: boolean = false): void {
        super.allSymbolsGoToCase(caseType, noWin);
        if (force && this.currentWins) {
            this.currentWins.forEach((win) => {
                if (win.trigger === Communication.Game.WinTrigger.Way) {
                    for (let i = 0; i < win.symbols.length; i++) {
                        if (win.symbols && win.symbols[i] !== 10) {
                            continue;
                        }
                        for (let j = 0; j < this.symbolCount; j++) {
                            if (win.mask && win.mask[i] && Math.pow(2, j) == win.mask[i] && this.currentSymbols[i][j]) {
                                this.currentSymbols[i][j].goToCase("EMPTY");
                            }
                        }
                    }
                }
            });
        }
    }

    protected showFirstWinsStep(): void {
        // PlayList.stop("SND_BaseLoop", 10);

        const win = this.currentWins[this.currentCounterStep];
        if (win.type != Communication.Game.WinType.Cash) {
            this.currentCounterStep++;
            this.showFirstWinsStep();

            return;
        }

        this.allPaylinesGoToCase(Payline.CaseNowin);
        this.allSymbolsGoToCase(Symbol.CaseNowin, true);

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winPaylineGoToCase(i, Payline.CaseNormal);
            this.winSymbolsGoToCase(i, Symbol.CaseNormal, false);
        }

        this.winPaylineGoToCase(this.currentCounterStep, Payline.CaseWin);
        const symbols = this.winSymbolsGoToCase(this.currentCounterStep, Symbol.CaseWin, false);

        this.runCounterStep();

        if (this.currentCounterStep == 0) {
            PlayList.setVolume("SND_BaseLoop", 0.05, 5);
        }

        this.currentCounterStep++;

        if (this.currentWin / BetManager.Bet > 1) {
            this.startTimer(
                this.currentCounterStep < this.currentWins.length
                    ? WinAnimatorExtended.ShowFirstWinsStep
                    : WinAnimatorExtended.FinishWin,
                this._paylineWinDelay,
            );
        } else {
            this.startTimer(
                this.currentCounterStep < this.currentWins.length
                    ? WinAnimatorExtended.ShowFirstWinsStep
                    : WinAnimatorExtended.FinishWin,
                120,
            );
        }
    }

    private showFirstWinsSum() {
        if (this.currentWin > 0) {
            this.wildMask.forEach((value, i) => {
                if (value) {
                    this._wildsExpanded[i].goToCase("ANIMATE");
                    //this._wildsExpanded[index].goToCase("WIN_ALL", false, d);
                    this._wildsExpanded[i].goToCase("ON", false, i * WinAnimatorExtended.ExpandStaggerWin);
                    this.currentSymbols[i].forEach((symbol, index) =>
                        symbol
                            ? symbol.goToCase(
                                  "EMPTY",
                                  false,
                                  i * WinAnimatorExtended.ExpandStaggerWin +
                                      index * (WinAnimatorExtended.ExpandSymbolDuration / this.symbolCount),
                              )
                            : null,
                    );
                }
            });
        }

        if (this.wildWin) {
            PlayList.play("SND_BaseExpandWild");
        }

        this.startTimer(WinAnimatorExtended.ShowFirstWinsSumAfter, WinAnimatorExtended.DelaySumAfter);
    }

    private showFirstWinsSumAfter() {
        this.allPaylinesGoToCase(Payline.CaseNowin);
        this.allSymbolsGoToCase(Symbol.CaseNowin, true, true);

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winSymbolsGoToCase(i, Symbol.CaseNormal, false);
        }
        for (let i = 0; i < this.currentWins.length; i++) {
            this.winPaylineGoToCase(i, Payline.CaseWin);
            this.winSymbolsGoToCase(i, Symbol.CaseWin, false);
        }

        this.wildMask.forEach((value, i) => {
            if (value) {
                this._wildsExpanded[i].goToCase("ANIMATE");
                this._wildsExpanded[i].goToCase("WIN_ALL", false, i * WinAnimatorExtended.ExpandStaggerWin);
            }
        });

        let animation: TimelineElement = null;
        if (this.currentWin < BetManager.Bet && this.animationsRegular[0]) {
            animation = this.animationsRegular[0];
        } else if (this.currentWin > BetManager.Bet && this.animationsRegular[2]) {
            animation = this.animationsRegular[2];
        } else {
            return;
        }

        this.currentAnimationIndex = this.getAnimationIndex();

        if (this.currentAnimationIndex >= 2) {
            PlayList.setVolume("SND_BaseLoop", 0, 10);
            PlayList.play("SND_HWIntro");
            this.currentAnimationIndex = this.getAnimationIndex();
            this.currentAnimationFinished = false;
            this.animationsRegularStopped = true;
            animation = this.animations[this.currentAnimationIndex];
            animation.visible = true;
            this.animationsRegular[0].visible = false;
            this.animationsRegular[2].visible = false;
            this.startTimer(WinAnimatorExtended.ShowAnimationsDelayed, 60);
            return;
        } else {
            this.playWinSound();
            PlayList.setVolume("SND_BaseLoop", 0.15, 5);
            PlayList.loop("SND_BaseCreditboxLoop", 10);
        }

        // for timing purposes
        this.animations[this.currentMultiplierIndex].play(this.currentWin);

        let amountFrom = "0";
        let amountTo = String(this.currentWin);

        this.animationsRegularStopped = false;
        animation.visible = true;

        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            ImageTextCounterElement.Round = true;
            animation.play(
                parseInt(NumberUtils.devide(amountTo, BetManager.CoinSize).toString()),
                parseInt(NumberUtils.devide(amountFrom, BetManager.CoinSize).toString()),
                WinAnimatorExtended.SumDuration,
            );
        } else {
            ImageTextCounterElement.Round = false;
            animation.play(parseFloat(amountTo), parseFloat(amountFrom), WinAnimatorExtended.SumDuration);
        }

        const step = 0.3;

        Tweens.removeTweens(animation, "all");
        Tweens.alphaTween(animation, {
            alphas: [animation.alpha, 1],
            tweenDuration: 20,
        });
        Tweens.scaleTween(animation, {
            tweenValues: [
                new PIXI.Point(0.7 + step, 0.7 + step),
                new PIXI.Point(0.75 + step, 0.75 + step),
                new PIXI.Point(0.75 + step, 0.75 + step),
                new PIXI.Point(0.75 + step, 0.75 + step),
                new PIXI.Point(0.7 + step, 0.7 + step),
            ],
            tweenDuration: 55,
            tweenEase: "backInOut",
            callbackFunction: function () {
                PlayList.stop("SND_BaseCreditboxLoop");
                PlayList.play("SND_BaseCreditboxEnd");
                PlayList.play("SND_BaseWinline99");
                this.animationsRegularStopped = true;
                animation.goToOutro();
                Tweens.alphaTween(animation, {
                    alphas: [1, 0],
                    tweenDuration: 20,
                    tweenDelay: 60,
                    callbackFunction: function () {
                        animation.visible = false;
                    },
                });
            },
        });
    }

    private showWinsDelayed() {
        this.allPaylinesGoToCase(Payline.CaseNowin);
        this.allSymbolsGoToCase(Symbol.CaseNowin, true, true);

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winPaylineGoToCase(i, Payline.CaseWin);
            this.winSymbolsGoToCase(i, Symbol.CaseWin, false);
        }
    }

    private getCounterStep(): number {
        return (0.8 - 0.5) / this.currentWins.length + 0.01;
    }

    protected runCounterStep(): void {
        let animation: TimelineElement = null;
        if (this.currentWin < BetManager.Bet && this.animationsRegular[0]) {
            animation = this.animationsRegular[0];
        } else if (this.currentWin > BetManager.Bet && this.animationsRegular[2]) {
            animation = this.animationsRegular[2];
        } else {
            return;
        }

        PlayList.play("SND_BaseWinlineHit");
        PlayList.play("SND_BaseWinline0" + Math.min(this.currentCounterStep + 1, 9));

        let amountFrom = "0";
        let amountTo = "0";

        this.animationsRegularStopped = false;
        animation.visible = true;

        for (let i = 0; i <= this.currentCounterStep; i++) {
            const win = this.currentWins[i];

            if (win.type === Communication.Game.WinType.Cash) {
                if (i < this.currentCounterStep) {
                    amountFrom = NumberUtils.add(amountFrom, this.currentWins[i].amount);
                }

                amountTo = NumberUtils.add(amountTo, this.currentWins[i].amount);
            }
        }

        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            ImageTextCounterElement.Round = true;
            animation.play(
                parseInt(NumberUtils.devide(amountTo, BetManager.CoinSize).toString()),
                parseInt(NumberUtils.devide(amountFrom, BetManager.CoinSize).toString()),
                0,
            );
        } else {
            ImageTextCounterElement.Round = false;
            animation.play(parseFloat(amountTo), parseFloat(amountFrom), 0);
        }

        const step = this.getCounterStep();

        Tweens.removeTweens(animation, "all");
        Tweens.alphaTween(animation, {
            alphas: [animation.alpha, 1],
            tweenDuration: 20,
        });
        Tweens.scaleTween(animation, {
            tweenValues: [
                new PIXI.Point(0.7 + step * this.currentCounterStep, 0.7 + step * this.currentCounterStep),
                new PIXI.Point(
                    0.75 + step * (this.currentCounterStep + 1),
                    0.75 + step * (this.currentCounterStep + 1),
                ),
                new PIXI.Point(
                    0.75 + step * (this.currentCounterStep + 1),
                    0.75 + step * (this.currentCounterStep + 1),
                ),
                new PIXI.Point(
                    0.75 + step * (this.currentCounterStep + 1),
                    0.75 + step * (this.currentCounterStep + 1),
                ),
                new PIXI.Point(0.7 + step * (this.currentCounterStep + 1), 0.7 + step * (this.currentCounterStep + 1)),
            ],
            tweenDuration: 55,
            tweenEase: "backInOut",
        });
    }

    protected runCounterOutro(reset: boolean = false): void {
        if (this.currentMultiplierIndex > 1) {
            return;
        }

        this.playWinSound();

        let animation: TimelineElement = null;
        if (this.currentWin < BetManager.Bet && this.animationsRegular[0]) {
            animation = this.animationsRegular[0];
        } else if (this.currentWin > BetManager.Bet && this.animationsRegular[2]) {
            animation = this.animationsRegular[2];
        } else {
            return;
        }

        let counterSize = 0.7 + this.getCounterStep() * (reset ? this.currentWins.length : this.currentCounterStep);

        this.animationsRegularStopped = true;
        animation.scale.x = animation.scale.y = counterSize;

        if (reset) {
            Tweens.alphaTween(animation, {
                alphas: [1, 0],
                tweenDuration: 20,
                tweenDelay: 20,
                callbackFunction: function () {
                    animation.visible = false;
                },
            });

            if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                ImageTextCounterElement.Round = true;
                animation.goToOutro(
                    parseInt(NumberUtils.devide(this.currentWin, BetManager.CoinSize).toString()),
                    true,
                );
            } else {
                ImageTextCounterElement.Round = false;
                animation.goToOutro(this.currentWin, true);
            }
        } else {
            Tweens.alphaTween(animation, {
                alphas: [1, 0],
                tweenDuration: 20,
                tweenDelay: 60,
                callbackFunction: function () {
                    animation.visible = false;
                },
            });

            if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                ImageTextCounterElement.Round = true;
                animation.goToOutro(parseInt(NumberUtils.devide(this.currentWin, BetManager.CoinSize).toString()));
            } else {
                ImageTextCounterElement.Round = false;
                animation.goToOutro(this.currentWin);
            }
        }
    }

    protected winSymbolsGoToCaseAsCustom(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];

        for (let i = 0; i < this.reelCount; i++) {
            const symbol = this.currentSymbols[i][1];
            symbol.goToCase(caseType);

            this.symbolsContainer.addChild(symbol);

            symbols.push(symbol);
        }

        return symbols;
    }

    onTimerComplete(): void {
        this.timer.stop();

        switch (this.timerStatus) {
            case WinAnimatorExtended.ShowFirstWinsStep:
                this.showFirstWinsStep();
                break;
            case WinAnimatorExtended.ShowFirstWinsSum:
                this.showFirstWinsSum();
                break;
            case WinAnimatorExtended.ShowFirstWinsSumAfter:
                this.showFirstWinsSumAfter();
                break;
            case WinAnimatorExtended.ShowAnimationsDelayed:
                this.startAnimation();
                break;
            case WinAnimatorExtended.ShowAnimationsDelayedBefore:
                this.showWinsDelayed();
                this.startTimer(WinAnimatorExtended.ShowAnimationsDelayed, 80);
                break;
            case WinAnimatorExtended.EndFirstWinsStep:
                this.emit(WinAnimator.FinishWinAnimator);
                this.currentCycle = 0;

                this.cycleWins(true);
                break;
            case WinAnimatorExtended.FinishWin:
                this.finishWin();
                break;
            default:
                super.onTimerComplete();
                break;
        }
    }

    protected finishWin(): void {
        this.emit(WinAnimator.FinishWinCalculation);

        this.runCounterOutro();

        this.startTimer(WinAnimatorExtended.EndFirstWinsStep, this._paylineWinEndDelay);
    }

    protected showCustomWin(): boolean {
        return false;
    }

    //******************************************************************************************************************
    //  RESET
    //******************************************************************************************************************

    public reset(showLastSymbols: boolean, spin = false): boolean {
        this._paylines.visible = false;

        if (spin) {
            this._lineTable.goToCase("SHOW_9");
            this._lineNumber.goToCase("RESET");
            this._crosses.goToCase("HIDE");
            //            this.setActiveElementCaseArray([ "HIDE_81" ]);
            AmbientBackground.changeAmbientBackground("base");
            this.setActiveElementCaseArray([WinAnimatorExtended.CaseNormal]);
        } else {
            if (this.wildWin) {
                if (this.wildMask.indexOf(false) >= 0) {
                    this._lineTable.goToCase("SHOW_81_INIT");
                    this._crosses.goToCase("SHOW_INIT_81");
                } else {
                    this._lineTable.goToCase("INIT_HIDE");
                }
                AmbientBackground.changeAmbientBackground("color-1", 0, 0);
                this.setActiveElementCaseArray(["PURPLE", "NORMAL"]);
            } else {
                this._lineTable.goToCase("SHOW_9");
                AmbientBackground.changeAmbientBackground("base");
            }
            this._lineNumber.goToCase("RESET");
        }

        this.animations[0].goToOutro();
        this.animations[1].goToOutro();

        this._label500x.goToCase("HIDE");

        if (this.currentMultiplierIndex < 2) {
            this.animationsRegular.forEach((animation, index) => {
                if (!this.animationsRegularStopped && animation.visible) {
                    if (index != 1) {
                        let counterSize = 1;
                        animation.scale.x = animation.scale.y = counterSize;
                        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                            ImageTextCounterElement.Round = true;
                            animation.play(
                                parseInt(NumberUtils.devide(this.currentWin, BetManager.CoinSize).toString()),
                                parseInt(NumberUtils.devide(this.currentWin, BetManager.CoinSize).toString()),
                                0,
                            );
                            animation.goToOutro(
                                parseInt(NumberUtils.devide(this.currentWin, BetManager.CoinSize).toString()),
                                true,
                            );
                        } else {
                            ImageTextCounterElement.Round = false;
                            animation.play(this.currentWin, this.currentWin, 0);
                            animation.goToOutro(this.currentWin, true);
                        }
                        Tweens.removeTweens(animation);
                        // for timing purposes
                        Tweens.alphaTween(animation, {
                            alphas: [1, 0],
                            tweenDuration: 1,
                            tweenDelay: 50,
                            callbackFunction: function () {
                                animation.visible = false;
                            },
                        });
                    } else {
                        animation.goToOutro(this.currentWin);
                    }
                }
            });
        }
        this.animationsRegularStopped = true;

        switch (this.timerStatus) {
            case WinAnimatorExtended.ShowFirstWinsStep:
                this.runCounterOutro(true);
                break;
            case WinAnimatorExtended.ShowFirstWinsSum:
                break;
            case WinAnimatorExtended.FinishWin:
                this.runCounterOutro(true);
                this.currentCounterStep = null;
                this.timerStatus = null;
                this.currentCycle = 0;
                break;
            case WinAnimatorExtended.EndFirstWinsStep:
                this.currentCounterStep = null;
                this.timerStatus = null;
                this.currentCycle = 0;
                break;
        }

        if (this.currentAnimationIndex != null) {
            if (this.currentAnimationFinished === false) {
                this.stopAnimation();
            } else {
                // only for HW index 2,3,4 - was FINISHED but was not HW SOFT outro
                if (!this._wasOutro && this.currentAnimationIndex >= 2 && this.currentAnimationIndex <= 4) {
                    if (this._groupTweensWA) {
                        this._groupTweensWA.goToCase(GroupTweens.CaseHighwinsHardOutro);
                    }
                }
            }
        }
        this._wasOutro = false;

        this.allSymbolsGoToCase(showLastSymbols ? Symbol.CaseNormal : Symbol.CaseEmpty, false, spin);
        this.sortSymbols();

        this.wildMask.forEach((value, i) => {
            if (value) {
                if (spin) {
                    this._wildsExpanded[i].goToCase("VISIBLE");
                    this._wildsExpanded[i].goToCase("SPIN", false, i * 3);
                } else {
                    this._wildsExpanded[i].goToCase("STATIC");
                }
                this.currentSymbols[i].forEach((symbol) => (symbol ? symbol.goToCase("EMPTY") : null));
            }
        });

        if (spin) {
            this.wildMask = [false, false, false, false];
            this.wildWin = false;
        }

        return super.reset(showLastSymbols, spin);
    }

    protected resetAnimations(): void {
        super.resetAnimations();
        for (let i = 0; i < this.animationsRegular.length; i++) {
            const animation = this.animationsRegular[i];
            animation.visible = false;
            animation.stop();
        }
    }

    public hideExpandWilds(spin: boolean): void {
        if (this._parent) {
            this._parent.addChildAt(this._wildExpanded, this._wildDepthLow);
        }
    }

    //******************************************************************************************************************
    //  ACTIVE ELEMENTS
    //******************************************************************************************************************

    /**
     * Attempts to switch all Active Elements to the earliest case from array the element can be switched to.
     * Elements with no case from the list are ignored.
     * @param s Array of names of cases to try to switch to
     * @param emitEvent
     * @param delay Switch delay
     */
    public setActiveElementCaseArray(s: Array<string>, emitEvent: boolean = false, delay?: number) {
        if (!s.length) {
            return;
        }
        if (!this.visible) {
            return;
        }
        this._activeElementsCase = s[0];
        this._activeElements.forEach((element) => {
            for (let i = 0; i < s.length; i++) {
                if (element.hasCase(s[i])) {
                    element.goToCase(s[i], emitEvent, delay);
                    break;
                }
            }
        });
    }

    /**
     * Attempts to switch all Active Elements to the given case if they have this case.
     * Elements without the case are ignored.
     * @param s Case name
     * @param emitEvent
     * @param delay
     */
    // private setActiveElementCase(s: string, emitEvent: boolean = false, delay?: number) {
    //     if(!this.visible) {
    //         return;
    //     }
    //
    //     this._activeElementsCase = s;
    //     this._activeElements.forEach(element => {
    //         if(element.hasCase(s)) {
    //             element.goToCase(s, emitEvent, delay);
    //         }
    //     });
    // }

    /**
     * Returns last case switched to, or the first case from a list switched to.
     */
    // private getActiveElementCase(): string {
    //     return this._activeElementsCase;
    // }

    protected getCycleWinsDelay(): number {
        return 120;
    }

    protected cycleWins(isFirst: boolean = false) {
        if (!this.currentWins) {
            return;
        }

        if (DataManager.Autoplay == false) {
            PlayList.setVolume("SND_BaseLoop", 0, 10);
        }

        PlayList.play("SND_BaseRecapWinline");

        if (!this.wildWin) {
            if (isFirst) {
                this._lineTable.goToCase("HIDE_LEFT");
            }
            if (this.currentWins && this.currentWins.length == 1) {
                if (isFirst) {
                    this._lineNumber.goToCase("PAYLINE_" + this.currentWins[0].paylineId + "_SINGLE");
                }
            } else if (
                this.currentWins[this.currentCycle].paylineId ||
                this.currentWins[this.currentCycle].paylineId == 0
            ) {
                this._lineNumber.goToCase("PAYLINE_" + this.currentWins[this.currentCycle].paylineId);
            }
        }

        super.cycleWins(isFirst);

        if (
            this.currentWins &&
            this.currentWins.filter((win) => win.trigger === Communication.Game.WinTrigger.Scatter).length
        ) {
            this.value.visible = false;
            if (isFirst) {
                this._label500x.goToCase("SHOW");
            }
        }
    }

    public calculationCounter(actualCounterValue: string): void {
        // console.log("....calc:..." + actualCounterValue);

        if (
            actualCounterValue == null ||
            actualCounterValue == "" ||
            this._bgWinsIndex == null ||
            this._bgScaleX === 0 ||
            this._bgScaleXMul === 0 ||
            this._bgElementName == null
        ) {
            return;
        }

        let counterLength: number = actualCounterValue.length;
        let aniIndex: number = this.getAnimationIndex();

        // ================ CYCLE ============================================
        if (this.value != null) {
            let elements: ContainerElement = this.value.getElements();

            if (counterLength > 0 && elements) {
                let bg = elements.getChildElementByName(this._bgElementName) as ContainerElement;
                if (bg) {
                    if (this._bgScaleXRankTo == 0 || this._bgScaleXRankTo < this._bgScaleXRankFrom) {
                        this._bgScaleXRankTo = counterLength;
                    }

                    if (counterLength <= this._bgScaleXRankFrom) {
                        // reset to default scale
                        bg.scale.x = this._bgScaleX;
                    } else if (counterLength <= this._bgScaleXRankTo) {
                        let newScaleX: number =
                            this._bgScaleX + (counterLength - this._bgScaleXRankFrom) * this._bgScaleXMul; // basic scale + (add scale * rank)
                        bg.scale.x = newScaleX;
                    } else {
                        let newScaleX: number =
                            this._bgScaleX + (this._bgScaleXRankTo - this._bgScaleXRankFrom) * this._bgScaleXMul; // basic scale + (add scale * rank)
                        bg.scale.x = newScaleX;
                    }
                }
            }
        }
        // ========================================================================

        if (this._bgWinsIndex.indexOf(aniIndex) === -1 || aniIndex == null) {
            return;
        }

        let elements: ContainerElement = this.animations[aniIndex].getElements();

        if (counterLength > 0 && elements) {
            let bg = elements.getChildElementByName(this._bgElementName) as ContainerElement;
            if (bg) {
                // let middle = <ContainerElement>(<ContainerElement>conBg).getChildElementByName("Middle");

                // if(middle && middle instanceof PIXI.Sprite) {
                if (this._bgScaleXRankTo == 0 || this._bgScaleXRankTo < this._bgScaleXRankFrom) {
                    this._bgScaleXRankTo = counterLength;
                }

                if (counterLength <= this._bgScaleXRankFrom) {
                    // reset to default scale
                    bg.scale.x = this._bgScaleX;
                    // middle.scale.x = this._bgScaleX;
                    // left.position.x = (middle.pivot.x * (this._bgScaleX - 1)) * -1;
                    // right.position.x = middle.pivot.x * (this._bgScaleX - 1);
                } else if (counterLength <= this._bgScaleXRankTo) {
                    let newScaleX: number =
                        this._bgScaleX + (counterLength - this._bgScaleXRankFrom) * this._bgScaleXMul; // basic scale + (add scale * rank)
                    bg.scale.x = newScaleX;
                    // (<PIXI.Sprite>middle).scale.x = newScaleX;
                    // left.position.x = (middle.pivot.x * (newScaleX - 1)) * -1;
                    // right.position.x = middle.pivot.x * (newScaleX - 1);

                    // console.log(newScaleX);
                    // console.log(middle.width);
                    // console.log(left.position);

                    //console.log(".......newScaleX: " + newScaleX);
                } else {
                    let newScaleX: number =
                        this._bgScaleX + (this._bgScaleXRankTo - this._bgScaleXRankFrom) * this._bgScaleXMul; // basic scale + (add scale * rank)
                    bg.scale.x = newScaleX;
                    // (<PIXI.Sprite>middle).scale.x = newScaleX;
                    // left.position.x = (middle.pivot.x * (newScaleX - 1)) * -1;
                    // right.position.x = middle.pivot.x * (newScaleX - 1);
                }
                // }
            }
        }
    }
}

export const WinAnimatorExtendedAttributes = {
    CYCLE_ANIMATION: "CycleAnimation",
    ACTIVE_ELEMENTS: "ActiveElements",

    /**
     * Dynamic scale for win counter.
     * BgElementName - name of scale element (tween object: ContainerElement...)
     * BgWinsIndex - indexes of all accepted wins (which wins will be scaling)
     * BgScaleX - base (start) scale of element
     * BgScaleXMultiplier - multiplication of rank scaleX by one step
     * BgRankFrom - start scale rank of counter
     * BgRankTo - end scale rank of counter
     * @property SymbolToWildTimeY
     * @type number
     * @default 1
     */
    BG_ELEMENT_NAME: "BgElementName",
    BG_WINS_INDEX: "BgWinsIndex",
    BG_SCALE_X: "BgScaleX",
    BG_SCALE_X_MULTIPLIER: "BgScaleXMultiplier",
    BG_RANK_FROM: "BgRankFrom",
    BG_RANK_TO: "BgRankTo",

    WILD_DEPTH_LOW: "WildDepthLow",
    WILD_DEPTH_HIGH: "WildDepthHigh",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimatorExtended.ts
 **/